// useGetDocument.ts
import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'
import { PandaDocument, PandaRecipient } from 'types'

const pandaAccessToken =
  import.meta.env.VITE_ENV === 'local'
    ? '44c682867cd90875b3ba53db7280c56e1a4d6068'
    : localStorage.getItem('panda_access_token')

// runtime guard to normalize recipients
function normalizeDoc(
  doc: PandaDocument | (Omit<PandaDocument, 'recipients'> & { recipients: unknown })
): PandaDocument {
  const raw = doc as any
  const recs: unknown = raw.recipients
  return {
    ...raw,
    recipients: Array.isArray(recs) ? (recs as PandaRecipient[]) : [], // <- always array
  }
}

const getDocumentById = async (documentId: string): Promise<PandaDocument> => {
  if (!pandaAccessToken) {
    throw new Error('Panda authentication token is missing or expired. Please log in again.')
  }

  const url = `${baseUrl}/aws/pandadoc/documents/${documentId}`
  const res = await axios.get<PandaDocument>(url, {
    headers: { Authorization: `${pandaAccessToken}` }, // or `Bearer ${token}` if your API expects it
  })
  return normalizeDoc(res.data)
}

export const useGetDocument = (documentId?: string, queryKey: unknown[] = ['document', documentId]) => {
  const {
    data: document,
    isLoading: isDocumentLoading,
    isError: isDocumentError,
  }: UseQueryResult<PandaDocument> = useQuery({
    queryKey,
    queryFn: () => getDocumentById(documentId as string),
    enabled: !!documentId,
    staleTime: 60_000, // 1 min — allow refetch after status transitions (send, revert)
    // You can also normalize in `select` if you prefer:
    // select: d => normalizeDoc(d),
  })

  return { document, isDocumentLoading, isDocumentError }
}
